import { ImageCollectionInterface } from '@app/_interfaces/image.interface';
import { ProductBooking } from '@app/_interfaces/product';

export class Products {
    type: string;
    name: string;
    code: string;
    companyId: string;
    companyData?: object;
    category?: Array<string>;
    description: string;
    price: number | Array<number>;
    discountValue: object;
    quantityDiscount: any;
    features: [string, string][];
    options: Array<string>;
    combinations: any;
    images: ImageCollectionInterface;
    vat: string;
    status: string;
    booking?: ProductBooking;

    constructor(data?: Partial<Products>) {
        if (data) {
            Object.assign(this, data);
        }
    }

    // Helper method to check if product is bookable
    isBookable(): boolean {
        return this.booking?.forBooking || false;
    }

    // Helper method to get booking type display name
    getBookingTypeDisplayName(): string {
        if (!this.booking?.bookingType) return '';

        switch (this.booking.bookingType) {
            case 'period':
                return 'Period Booking';
            case 'timeSlots':
                return 'Time Slots';
            case 'perDay':
                return 'Per Day';
            default:
                return '';
        }
    }

    // Helper method to validate booking configuration
    isValidBookingConfig(): boolean {
        if (!this.booking?.forBooking) return true;

        const {
            bookingType,
            minBookingDays,
            maxBookingDays,
            timeSlotDuration,
            timeSlotStartTime,
            timeSlotEndTime
        } = this.booking;

        switch (bookingType) {
            case 'period':
                return (
                    minBookingDays > 0 &&
                    maxBookingDays > 0 &&
                    minBookingDays <= maxBookingDays
                );

            case 'timeSlots':
                return (
                    timeSlotDuration > 0 &&
                    timeSlotStartTime &&
                    timeSlotEndTime &&
                    timeSlotStartTime < timeSlotEndTime
                );

            case 'perDay':
                return (
                    this.booking.advanceBookingDays >= 0 &&
                    this.booking.maxBookingsPerUser > 0
                );

            default:
                return false;
        }
    }

    // Helper method to get minimum booking price
    getMinBookingPrice(): number {
        if (!this.booking?.forBooking) return 0;

        switch (this.booking.bookingType) {
            case 'period':
                return typeof this.price === 'number'
                    ? this.price * this.booking.minBookingDays
                    : 0;

            case 'timeSlots':
                return this.booking.pricePerTimeSlot;

            case 'perDay':
                return typeof this.price === 'number' ? this.price : 0;

            default:
                return 0;
        }
    }

    // Helper method to calculate available slots per day for time slots
    getAvailableSlotsPerDay(): number {
        if (
            !this.booking?.forBooking ||
            this.booking.bookingType !== 'timeSlots'
        ) {
            return 0;
        }

        const {
            timeSlotStartTime,
            timeSlotEndTime,
            timeSlotDuration,
            timeSlotInBetweenHold
        } = this.booking;

        if (!timeSlotStartTime || !timeSlotEndTime) return 0;

        const startTime = this.timeStringToMinutes(timeSlotStartTime);
        const endTime = this.timeStringToMinutes(timeSlotEndTime);
        const slotDurationMinutes = timeSlotDuration * 60;
        const breakMinutes = timeSlotInBetweenHold * 60;

        const totalMinutes = endTime - startTime;
        const slotWithBreak = slotDurationMinutes + breakMinutes;

        return Math.floor(totalMinutes / slotWithBreak);
    }

    // Helper method to convert time string to minutes
    private timeStringToMinutes(timeString: string): number {
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours * 60 + minutes;
    }

    // Helper method to get booking constraints text
    getBookingConstraintsText(): string {
        if (!this.booking?.forBooking) return '';

        const constraints: string[] = [];

        switch (this.booking.bookingType) {
            case 'period':
                constraints.push(`Min: ${this.booking.minBookingDays} days`);
                constraints.push(`Max: ${this.booking.maxBookingDays} days`);
                break;

            case 'timeSlots':
                constraints.push(`${this.booking.timeSlotDuration}h slots`);
                constraints.push(
                    `${this.booking.advanceBookingHours}h advance booking`
                );
                constraints.push(
                    `Available: ${this.booking.timeSlotStartTime} - ${this.booking.timeSlotEndTime}`
                );
                break;

            case 'perDay':
                constraints.push(
                    `${this.booking.advanceBookingDays} days advance booking`
                );
                constraints.push(
                    `Max ${this.booking.maxBookingsPerUser} bookings per user`
                );
                break;
        }

        if (this.booking.requireApproval) {
            constraints.push('Requires approval');
        }

        if (this.booking.allowCancellation) {
            constraints.push('Cancellation allowed');
        }

        return constraints.join(' • ');
    }
}

export enum ProductOptionType {
    SelectBox = 'S',
    Checkbox = 'C',
    RadioGroup = 'R',
    ColorGroup = 'CL',
    Text = 'T',
    File = 'F',
    Date = 'D'
}

// Add booking-related enums
export enum BookingType {
    Period = 'period',
    TimeSlots = 'timeSlots',
    PerDay = 'perDay'
}

export enum BookingStatus {
    Pending = 'pending',
    Approved = 'approved',
    Rejected = 'rejected',
    Cancelled = 'cancelled',
    Completed = 'completed'
}
